import React, { createContext, useContext, useState, useCallback } from 'react';
import axiosInstance from '../lib/auth/axiosApi';
import { toast } from 'react-toastify';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], subtotal: '0.00', shipping_cost: '0.00', total: '0.00', item_count: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      const res = await axiosInstance.get('store/cart/');
      setCart(res.data);
    } catch (e) {
      // User might not be logged in
    }
  }, []);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('store/cart/add_item/', { product_id: productId, quantity });
      setCart(res.data);
      toast.success('Added to cart!');
      setLoading(false);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add to cart');
    }
    setLoading(false);
    return false;
  }, []);

  const updateCartItem = useCallback(async (itemId, quantity) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('store/cart/update_item/', { item_id: itemId, quantity });
      setCart(res.data);
    } catch (e) {
      toast.error('Failed to update cart');
    }
    setLoading(false);
  }, []);

  const removeCartItem = useCallback(async (itemId) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('store/cart/remove_item/', { item_id: itemId });
      setCart(res.data);
      toast.info('Removed from cart');
    } catch (e) {
      toast.error('Failed to remove item');
    }
    setLoading(false);
  }, []);

  const clearCart = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('store/cart/clear/');
      setCart(res.data);
    } catch (e) {
      toast.error('Failed to clear cart');
    }
    setLoading(false);
  }, []);

  const checkout = useCallback(async (shippingInfo, giftMessage = '') => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('store/checkout/create_session/', { shipping: shippingInfo, gift_message: giftMessage });
      // Redirect to Stripe
      window.location.href = res.data.checkout_url;
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Checkout failed');
      setLoading(false);
      return null;
    }
  }, []);

  return (
    <CartContext.Provider value={{
      cart, loading, fetchCart, addToCart, updateCartItem, removeCartItem, clearCart, checkout
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
